/**
 * Final Reel Display Component
 * Feature: Client-Side Video Processing
 *
 * Displays the generated final reel video with playback controls and download.
 */

import { memo, useState, useCallback, useRef } from 'react';
import { useQuery } from 'convex/react';
import { Play, Pause, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';

interface IFinalReelDisplayProps {
  reelId: Id<'reels'>;
  /** When true, video fills available viewport height */
  fullscreen?: boolean;
}

/**
 * Format bytes to human readable size
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Format duration in seconds to display string
 */
function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (mins > 0) {
    return `${mins}m ${secs}s`;
  }
  return `${secs}s`;
}

/**
 * Video Player Component for Final Reel
 */
function VideoPlayer({ videoUrl, fullscreen }: { videoUrl: string; fullscreen?: boolean }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const togglePlay = useCallback(() => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
  }, []);

  return (
    <div
      className={
        fullscreen
          ? 'relative overflow-hidden rounded-lg'
          : 'relative aspect-[9/16] w-full max-w-[240px] overflow-hidden rounded-lg bg-gray-900'
      }
    >
      <video
        ref={videoRef}
        src={videoUrl}
        className={
          fullscreen ? 'h-[calc(100dvh-8rem)] w-auto rounded-lg' : 'h-full w-full object-cover'
        }
        onEnded={handleEnded}
        playsInline
      />
      <button
        type="button"
        onClick={togglePlay}
        className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 transition-opacity hover:opacity-100"
        aria-label={isPlaying ? 'Pause video' : 'Play video'}
      >
        <div className="rounded-full bg-white/90 p-3">
          {isPlaying ? (
            <Pause className="h-6 w-6 text-gray-900" />
          ) : (
            <Play className="h-6 w-6 text-gray-900" />
          )}
        </div>
      </button>
    </div>
  );
}

/**
 * FinalReelDisplay Component
 */
function FinalReelDisplayComponent({ reelId, fullscreen }: IFinalReelDisplayProps) {
  const reelVideo = useQuery(api.reelVideos.getReelVideo, { reelId });

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = useCallback(async () => {
    if (!reelVideo?.videoUrl || isDownloading) return;

    setIsDownloading(true);
    try {
      const response = await fetch(reelVideo.videoUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `reel-${reelId}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      console.error('[FinalReelDisplay] Download failed');
    } finally {
      setIsDownloading(false);
    }
  }, [reelVideo?.videoUrl, reelId, isDownloading]);

  // Loading or no reel video yet
  if (reelVideo === undefined) {
    return null; // Loading state
  }

  if (reelVideo === null) {
    return null; // No reel video generated yet
  }

  if (!reelVideo.videoUrl) {
    return null; // Video URL not available
  }

  if (fullscreen) {
    return (
      <div className="flex h-[calc(100dvh-4rem)] flex-col items-center justify-center gap-4 px-4">
        <VideoPlayer videoUrl={reelVideo.videoUrl} fullscreen />
        <div className="flex items-center gap-6 text-sm text-foreground-muted">
          <span>{formatDuration(reelVideo.durationSeconds)}</span>
          <span>{formatFileSize(reelVideo.fileSizeBytes)}</span>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={handleDownload}
            disabled={isDownloading}
          >
            {isDownloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {isDownloading ? 'Downloading...' : 'Download'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 flex flex-col items-center space-y-3">
      <VideoPlayer videoUrl={reelVideo.videoUrl} />

      <div className="flex w-full max-w-[240px] items-center justify-between text-xs text-gray-600">
        <span>{formatDuration(reelVideo.durationSeconds)}</span>
        <span>{formatFileSize(reelVideo.fileSizeBytes)}</span>
      </div>

      <Button
        variant="outline"
        size="sm"
        className="w-full max-w-[240px] gap-2"
        onClick={handleDownload}
        disabled={isDownloading}
      >
        {isDownloading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Download className="h-4 w-4" />
        )}
        {isDownloading ? 'Downloading...' : 'Download'}
      </Button>
    </div>
  );
}

const FinalReelDisplay = memo(FinalReelDisplayComponent);

export { FinalReelDisplay };
